import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  RefreshCw, Send, Archive, AlertTriangle, MailOpen, Mail,
  Tag, StickyNote, RotateCcw, Loader2, Sparkles, PenSquare,
  CheckCircle2, XCircle, Bot, Search, X, ArrowLeft, ArrowRight, Settings,
  Zap, User, Users, Download, SendHorizonal, Paperclip, FileText, Image as ImageIcon,
  ChevronLeft, ChevronRight, Activity, Eye, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import AdminCompose from './AdminCompose';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TARGET_MAILBOX = 'promise.monday@thirdcultureleadership.co.uk';

const getCsrf = () => {
  const m = document.cookie.split('; ').find(r => r.startsWith('tcl_csrf='));
  return m ? m.split('=')[1] : '';
};

/* ─── Design tokens ──────────────────────────────────────── */
const BG     = '#071B3A';
const BG2    = '#0D2545';
const BG3    = '#040E1F';
const AGENT  = '#0B2247';
const GOLD   = '#C99316';
const GOLDB  = '#F0C654';
const CREAM  = '#FAF8F3';
const SERIF  = "'Cormorant Garamond', Georgia, serif";
const SANS   = "'IBM Plex Sans', system-ui, sans-serif";

const URGENCY = {
  high:   { bg: 'rgba(239,68,68,0.15)',   text: '#FCA5A5', border: '#EF4444',  dot: '#EF4444'  },
  medium: { bg: 'rgba(245,158,11,0.15)',  text: '#FCD34D', border: '#F59E0B',  dot: '#F59E0B'  },
  low:    { bg: 'rgba(16,185,129,0.15)',  text: '#6EE7B7', border: '#10B981',  dot: '#10B981'  },
};

const CAT_STYLE = {
  'Speaking':          { bg: 'rgba(201,147,22,0.2)',   text: '#F0C654', border: 'rgba(201,147,22,0.4)' },
  'Book':              { bg: 'rgba(147,51,234,0.2)',   text: '#E9D5FF', border: 'rgba(147,51,234,0.4)' },
  'Books':             { bg: 'rgba(147,51,234,0.2)',   text: '#E9D5FF', border: 'rgba(147,51,234,0.4)' },
  'Media':             { bg: 'rgba(59,130,246,0.2)',   text: '#93C5FD', border: 'rgba(59,130,246,0.4)' },
  'Course':            { bg: 'rgba(20,184,166,0.2)',   text: '#99F6E4', border: 'rgba(20,184,166,0.4)' },
  'Executive Coaching':{ bg: 'rgba(20,184,166,0.2)',   text: '#99F6E4', border: 'rgba(20,184,166,0.4)' },
  'Payment':           { bg: 'rgba(34,197,94,0.2)',    text: '#86EFAC', border: 'rgba(34,197,94,0.4)'  },
  'Partnership':       { bg: 'rgba(236,72,153,0.2)',   text: '#F9A8D4', border: 'rgba(236,72,153,0.4)' },
  'Subscriber':        { bg: 'rgba(20,184,166,0.15)',  text: '#99F6E4', border: 'rgba(20,184,166,0.3)' },
};
const getCat = c => CAT_STYLE[c] || { bg: 'rgba(100,116,139,0.2)', text: '#CBD5E1', border: 'rgba(100,116,139,0.4)' };
const getUrg = u => URGENCY[u] || URGENCY.medium;

const ALIASES = [
  { value: 'all',                                              label: 'All aliases' },
  { value: 'admin@thirdcultureleadership.co.uk',              label: 'Admin' },
  { value: 'books@thirdcultureleadership.co.uk',              label: 'Books' },
  { value: 'cfq@thirdcultureleadership.co.uk',                label: 'CFQ' },
  { value: 'contact@thirdcultureleadership.co.uk',            label: 'General Contact' },
  { value: 'courses@thirdcultureleadership.co.uk',            label: 'Courses' },
  { value: 'culturalfluency.book@thirdcultureleadership.co.uk', label: 'Cultural Fluency Book' },
  { value: 'hello@thirdcultureleadership.co.uk',              label: 'General / Brand' },
  { value: 'speaking@thirdcultureleadership.co.uk',           label: 'Speaking' },
  { value: 'support@thirdcultureleadership.co.uk',            label: 'Support' },
  { value: 'promise.monday@thirdcultureleadership.co.uk',     label: 'Founder / Direct' },
];

const STATUS_TABS = [
  { id: 'all',      label: 'All' },
  { id: 'unread',   label: 'Unread' },
  { id: 'replied',  label: 'Replied' },
  { id: 'archived', label: 'Archived' },
  { id: 'sent',     label: 'Sent' },
  { id: 'events',   label: 'Events' },
];

/* ─── helpers ───────────────────────────────────────────── */
const fmtDate = iso => {
  if (!iso) return '';
  const d = new Date(iso), now = new Date(), diff = now - d;
  if (diff < 86400000) return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  if (diff < 604800000) return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtFull = iso => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-GB', { dateStyle: 'full', timeStyle: 'short' });
};

const initials = (name, email) => {
  if (name && name.trim()) {
    const p = name.trim().split(' ');
    return (p.length >= 2 ? p[0][0] + p[1][0] : p[0][0]).toUpperCase();
  }
  return ((email || '?')[0]).toUpperCase();
};

/* ─── SubscribersPanel ──────────────────────────────────────────────────────── */
const STAGE_LABELS = {
  0: 'New', 1: 'Engaged', 2: 'Qualified', 3: 'Nurtured', 4: 'Converted',
};

const SubscribersPanel = ({ token, onComposeTo, onClose }) => {
  const [subs, setSubs]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filterConfirmed, setFilterConfirmed] = useState(false);
  const [page, setPage]         = useState(1);
  const PAGE = 20;

  const fetchSubs = useCallback(async (q, confirmed, pg) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ q, limit: PAGE, page: pg });
      if (confirmed) params.set('confirmed_only', 'true');
      const r = await fetch(API_URL + '/api/compose/contacts?' + params, {
        headers: { Authorization: 'Bearer ' + token }, credentials: 'include',
      });
      if (r.ok) {
        const d = await r.json();
        setSubs(d.contacts || []);
        setTotal(d.total || 0);
      }
    } catch {}
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    const t = setTimeout(() => { fetchSubs(search, filterConfirmed, page); }, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [search, filterConfirmed, page, fetchSubs]);

  const totalPages = Math.ceil(total / PAGE);

  return (
    <div
      data-testid="subscribers-panel"
      className="flex flex-col h-full"
      style={{ background: BG, borderLeft: '1px solid rgba(201,147,22,0.2)' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-3 flex-shrink-0"
        style={{ borderBottom: '1px solid rgba(201,147,22,0.15)', background: BG2 }}
      >
        <div className="flex items-center gap-2">
          <Users size={14} style={{ color: GOLD }} />
          <span style={{ fontFamily: SERIF, color: CREAM, fontSize: '16px', fontWeight: 600 }}>
            Mailing List
          </span>
          <span className="text-[10px] px-2 py-0.5 rounded-full ml-1" style={{ background: 'rgba(201,147,22,0.12)', color: GOLDB, border: '1px solid rgba(201,147,22,0.25)', fontFamily: SANS }}>
            {total}
          </span>
        </div>
        <button onClick={onClose} style={{ color: 'rgba(250,248,243,0.35)' }}>
          <X size={16} />
        </button>
      </div>

      {/* Search + filter */}
      <div className="px-4 py-3 space-y-2 flex-shrink-0" style={{ borderBottom: '1px solid rgba(201,147,22,0.08)' }}>
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg"
          style={{ background: 'rgba(250,248,243,0.04)', border: '1px solid rgba(250,248,243,0.08)' }}
        >
          <Search size={11} style={{ color: 'rgba(250,248,243,0.28)', flexShrink: 0 }} />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search name or email…"
            data-testid="subscribers-search"
            className="flex-1 bg-transparent text-xs focus:outline-none"
            style={{ color: CREAM, fontFamily: SANS }}
          />
          {search && <button onClick={() => { setSearch(''); setPage(1); }} style={{ color: 'rgba(250,248,243,0.3)' }}><X size={10} /></button>}
        </div>
        <div className="flex gap-2">
          {[['All', false], ['Confirmed', true]].map(([label, val]) => (
            <button
              key={label}
              onClick={() => { setFilterConfirmed(val); setPage(1); }}
              data-testid={'subscribers-filter-' + label.toLowerCase()}
              className="text-[10px] px-2.5 py-1 rounded-full transition-all"
              style={{
                background: filterConfirmed === val ? 'rgba(201,147,22,0.15)' : 'transparent',
                border: '1px solid ' + (filterConfirmed === val ? 'rgba(201,147,22,0.4)' : 'rgba(250,248,243,0.1)'),
                color: filterConfirmed === val ? GOLDB : 'rgba(250,248,243,0.4)',
                fontFamily: SANS,
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={18} style={{ color: 'rgba(201,147,22,0.4)' }} className="animate-spin" />
          </div>
        ) : subs.length === 0 ? (
          <div className="text-center py-12 px-6">
            <Users size={24} style={{ color: 'rgba(250,248,243,0.08)' }} className="mx-auto mb-3" />
            <p className="text-xs" style={{ color: 'rgba(250,248,243,0.22)', fontFamily: SANS }}>
              {search ? 'No matches' : 'No subscribers yet'}
            </p>
          </div>
        ) : (
          subs.map(sub => {
            const name = [sub.first_name, sub.last_name].filter(Boolean).join(' ');
            const ini  = (name ? name[0] : sub.email[0]).toUpperCase();
            return (
              <div
                key={sub.email}
                data-testid={'subscriber-row-' + sub.email}
                className="flex items-center gap-3 px-4 py-3 group transition-all"
                style={{ borderBottom: '1px solid rgba(201,147,22,0.06)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(13,37,69,0.5)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {/* Avatar */}
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                  style={{ background: sub.confirmed ? 'rgba(20,184,166,0.15)' : 'rgba(100,116,139,0.15)', color: sub.confirmed ? '#99F6E4' : '#94A3B8', fontFamily: SERIF }}
                >
                  {ini}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-xs truncate" style={{ color: CREAM, fontFamily: SANS, fontWeight: 500 }}>
                      {name || sub.email}
                    </span>
                    {sub.confirmed ? (
                      <span className="text-[9px] flex-shrink-0 px-1.5 py-0.5 rounded" style={{ background: 'rgba(20,184,166,0.12)', color: '#99F6E4', border: '1px solid rgba(20,184,166,0.2)' }}>✓</span>
                    ) : (
                      <span className="text-[9px] flex-shrink-0 px-1.5 py-0.5 rounded" style={{ background: 'rgba(245,158,11,0.1)', color: '#FCD34D', border: '1px solid rgba(245,158,11,0.2)' }}>unconfirmed</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-[10px] truncate" style={{ color: 'rgba(250,248,243,0.4)', fontFamily: SANS }}>{sub.email}</p>
                    {sub.nurture_stage != null && (
                      <span className="text-[9px] flex-shrink-0" style={{ color: 'rgba(201,147,22,0.5)', fontFamily: SANS }}>
                        {STAGE_LABELS[sub.nurture_stage] || 'Stage ' + sub.nurture_stage}
                      </span>
                    )}
                  </div>
                </div>

                {/* Email button */}
                <button
                  onClick={() => onComposeTo({ to_email: sub.email, to_name: name, from_alias: 'hello@thirdcultureleadership.co.uk' })}
                  data-testid={'compose-to-subscriber-' + sub.email}
                  className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold opacity-0 group-hover:opacity-100 transition-all"
                  style={{ background: 'rgba(201,147,22,0.15)', border: '1px solid rgba(201,147,22,0.35)', color: GOLDB, fontFamily: SANS }}
                >
                  <Mail size={10} /> Email
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          className="flex items-center justify-between px-4 py-2.5 flex-shrink-0"
          style={{ borderTop: '1px solid rgba(201,147,22,0.08)', fontFamily: SANS, fontSize: '10px', color: 'rgba(250,248,243,0.28)' }}
        >
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="disabled:opacity-30">← Prev</button>
          <span className="font-mono">{page} / {totalPages} · {total} total</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="disabled:opacity-30">Next →</button>
        </div>
      )}
    </div>
  );
};


const StatusHealthBar = ({ health, onSetup, setupLoading, onCompose, onRefresh, refreshing, unreadCount, onSync, syncLoading, onShowSubscribers, showSubscribers }) => {
  const isActive = health?.api_token_set && health?.mailbox_id_cached;
  return (
    <div
      data-testid="status-health-bar"
      style={{
        background: 'linear-gradient(to right, #071B3A, #0a2040)',
        borderBottom: '1px solid rgba(201,147,22,0.22)',
        boxShadow: '0 1px 0 rgba(0,0,0,0.5), 0 2px 20px rgba(0,0,0,0.3)',
        height: '62px',
        flexShrink: 0,
      }}
      className="px-5 flex items-center justify-between gap-3"
    >
      {/* Left: live dot + title + mailbox + unread */}
      <div className="flex items-center gap-3 min-w-0">
        <div className="relative flex-shrink-0">
          <div
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ background: isActive ? '#10B981' : '#F59E0B', boxShadow: '0 0 6px ' + (isActive ? 'rgba(16,185,129,0.8)' : 'rgba(245,158,11,0.8)') }}
          />
          {isActive && (
            <div className="absolute inset-0 rounded-full animate-ping" style={{ background: 'rgba(16,185,129,0.3)', animationDuration: '2s' }} />
          )}
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <h1 style={{ fontFamily: SERIF, color: CREAM, fontSize: '17px', fontWeight: 700, letterSpacing: '-0.015em', lineHeight: 1 }}>
              TCL Inbox
            </h1>
            {unreadCount > 0 && (
              <span
                className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
                style={{ background: 'rgba(20,184,166,0.18)', color: '#6EE7B7', border: '1px solid rgba(20,184,166,0.3)', fontFamily: SANS }}
              >
                {unreadCount} unread
              </span>
            )}
          </div>
          <p className="text-[10px] truncate mt-0.5" style={{ color: 'rgba(250,248,243,0.32)', fontFamily: SANS }}>
            {isActive ? (health?.mailbox || TARGET_MAILBOX) : 'Mailbox setup required · click Setup'}
          </p>
        </div>

        {/* Compact status chips */}
        <div className="hidden xl:flex items-center gap-1 ml-1">
          {[
            { label: 'API', ok: health?.api_token_set },
            { label: 'Hook', ok: health?.webhook_secret_set },
            { label: 'Box', ok: !!health?.mailbox_id_cached },
          ].map(({ label, ok }) => (
            <div key={label} className="flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: ok ? 'rgba(16,185,129,0.07)' : 'rgba(245,158,11,0.07)', border: '1px solid ' + (ok ? 'rgba(16,185,129,0.18)' : 'rgba(245,158,11,0.18)') }}>
              <div className="w-1 h-1 rounded-full" style={{ background: ok ? '#34d399' : '#fbbf24' }} />
              <span style={{ fontFamily: SANS, fontSize: '9px', color: ok ? 'rgba(52,211,153,0.7)' : 'rgba(251,191,36,0.6)' }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right: icon-only utility actions + primary buttons */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {/* Refresh */}
        <button
          onClick={onRefresh}
          data-testid="inbox-refresh-btn"
          className="p-2 rounded-lg transition-all"
          style={{ color: 'rgba(250,248,243,0.3)', border: '1px solid rgba(250,248,243,0.07)' }}
          title="Refresh inbox"
        >
          <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
        </button>

        {/* Pull Inbox */}
        <button
          onClick={() => onSync('INBOX')}
          disabled={!!syncLoading}
          data-testid="inbox-sync-inbox-btn"
          className="p-2 rounded-lg transition-all disabled:opacity-40"
          style={{ color: '#93C5FD', border: '1px solid rgba(59,130,246,0.18)', background: 'rgba(59,130,246,0.06)' }}
          title="Sync received messages from mail server"
        >
          {syncLoading === 'INBOX' ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
        </button>

        {/* Pull Sent */}
        <button
          onClick={() => onSync('Sent')}
          disabled={!!syncLoading}
          data-testid="inbox-sync-sent-btn"
          className="p-2 rounded-lg transition-all disabled:opacity-40"
          style={{ color: '#D8B4FE', border: '1px solid rgba(147,51,234,0.18)', background: 'rgba(147,51,234,0.06)' }}
          title="Sync sent messages from mail server"
        >
          {syncLoading === 'Sent' ? <Loader2 size={13} className="animate-spin" /> : <SendHorizonal size={13} />}
        </button>

        {/* Setup */}
        <button
          onClick={onSetup}
          disabled={setupLoading}
          data-testid="inbox-setup-btn"
          className="p-2 rounded-lg transition-all disabled:opacity-50"
          style={{ color: isActive ? 'rgba(52,211,153,0.65)' : '#F59E0B', border: '1px solid ' + (isActive ? 'rgba(16,185,129,0.18)' : 'rgba(245,158,11,0.25)'), background: isActive ? 'rgba(16,185,129,0.05)' : 'rgba(245,158,11,0.07)' }}
          title={isActive ? 'Mailbox active' : 'Setup mailbox connection'}
        >
          {setupLoading ? <Loader2 size={13} className="animate-spin" /> : <Settings size={13} />}
        </button>

        {/* Thin separator */}
        <div className="w-px h-6 mx-1" style={{ background: 'rgba(201,147,22,0.15)' }} />

        {/* Subscribers */}
        <button
          onClick={onShowSubscribers}
          data-testid="inbox-subscribers-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
          style={{
            background: showSubscribers ? 'rgba(201,147,22,0.14)' : 'transparent',
            border: '1px solid ' + (showSubscribers ? 'rgba(201,147,22,0.35)' : 'rgba(201,147,22,0.18)'),
            color: showSubscribers ? GOLDB : 'rgba(201,147,22,0.6)',
            fontFamily: SANS,
          }}
          title="Browse mailing list subscribers"
        >
          <Users size={12} />
          <span className="hidden sm:inline">Subscribers</span>
        </button>

        {/* Compose — primary CTA */}
        <button
          onClick={onCompose}
          data-testid="inbox-compose-btn"
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold transition-all active:scale-95"
          style={{ background: GOLD, color: BG, fontFamily: SANS, letterSpacing: '0.01em', boxShadow: '0 0 14px rgba(201,147,22,0.25), inset 0 1px 0 rgba(255,255,255,0.1)' }}
        >
          <PenSquare size={12} /> Compose
        </button>
      </div>
    </div>
  );
};

/* ─── EmailListItem ─────────────────────────────────────── */
const EmailListItem = ({ email, selected, onClick, isChecked, onCheckToggle }) => {
  const isSent   = email.direction === 'sent';
  const isUnread = !isSent && email.status === 'unread';
  const urg = getUrg(email.ai_urgency);
  const cat = getCat(email.category);
  const canSelect = !isSent && email.matched_subscriber?.nurture_stage != null;
  // For sent items display the first "To" recipient; for received, show sender
  const displayName = isSent
    ? ('→ ' + (Array.isArray(email.to) ? email.to[0] : (email.to || '')))
    : (email.from_name || email.from_email);
  const ini = isSent
    ? (Array.isArray(email.to) ? email.to[0][0] : '→').toUpperCase()
    : initials(email.from_name, email.from_email);

  return (
    <button
      onClick={() => onClick(email.id)}
      data-testid={'email-list-item-' + email.id}
      className="w-full text-left transition-colors duration-150"
      style={{
        borderBottom: '1px solid rgba(201,147,22,0.07)',
        borderLeft: '3px solid ' + (selected ? GOLD : (isSent ? 'rgba(20,184,166,0.3)' : (email.ai_urgency === 'high' ? '#EF4444' : 'transparent'))),
        background: selected ? BG2 : (isChecked ? 'rgba(201,147,22,0.04)' : 'transparent'),
      }}
    >
      <div className={selected ? 'px-4 py-3' : 'px-4 py-3 hover:bg-[#0D2545]/60'}>
        <div className="flex items-start gap-2.5">
          {/* Bulk-select checkbox — only for matched subscriber emails */}
          <div className="flex-shrink-0 mt-1" style={{ width: '16px' }}>
            {canSelect && (
              <div
                onClick={e => { e.stopPropagation(); onCheckToggle && onCheckToggle(email.id); }}
                data-testid={'email-checkbox-' + email.id}
                className="w-4 h-4 rounded flex items-center justify-center cursor-pointer transition-all"
                style={{
                  background: isChecked ? 'rgba(201,147,22,0.2)' : 'rgba(250,248,243,0.03)',
                  border: '1px solid ' + (isChecked ? GOLD : 'rgba(250,248,243,0.12)'),
                  boxShadow: isChecked ? '0 0 6px rgba(201,147,22,0.2)' : 'none',
                }}
              >
                {isChecked && <div className="w-2 h-2 rounded-sm" style={{ background: GOLDB }} />}
              </div>
            )}
          </div>
          {/* Avatar */}
          <div
            className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
            style={{
              background: isSent ? 'rgba(20,184,166,0.12)' : (isUnread ? 'rgba(201,147,22,0.16)' : 'rgba(250,248,243,0.05)'),
              color: isSent ? '#5EEAD4' : (isUnread ? GOLDB : 'rgba(250,248,243,0.3)'),
              fontFamily: SERIF,
            }}
          >
            {ini}
          </div>

          <div className="flex-1 min-w-0">
            {/* Row 1: name + time + unread dot */}
            <div className="flex items-center justify-between gap-1 mb-0.5">
              <span className="text-xs truncate" style={{ color: isUnread ? CREAM : (isSent ? '#5EEAD4' : 'rgba(250,248,243,0.52)'), fontWeight: isUnread || isSent ? 600 : 400, fontFamily: SANS }}>
                {displayName}
              </span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {isUnread && <div className="w-1.5 h-1.5 rounded-full" style={{ background: GOLDB, boxShadow: '0 0 4px rgba(240,198,84,0.6)' }} />}
                <span className="text-[10px] font-mono" style={{ color: 'rgba(250,248,243,0.25)' }}>{fmtDate(email.received_at)}</span>
              </div>
            </div>

            {/* Row 2: subject */}
            <p className="text-[11px] truncate mb-1" style={{ color: isUnread ? 'rgba(250,248,243,0.75)' : 'rgba(250,248,243,0.38)', fontFamily: SANS }}>
              {email.subject}
            </p>

            {/* Row 3: AI snippet */}
            {email.ai_summary && (
              <p className="text-[10px] truncate mb-1" style={{ color: 'rgba(250,248,243,0.2)', fontFamily: SANS, fontStyle: 'italic' }}>
                {email.ai_summary}
              </p>
            )}

            {/* Row 4: compact badges — only meaningful ones */}
            {(email.ai_urgency === 'high' || email.category || email.matched_subscriber || email.reply_count > 0) && (
              <div className="flex items-center gap-1 flex-wrap mt-0.5">
                {email.ai_urgency === 'high' && (
                  <span className="text-[8.5px] px-1 py-0.5 rounded font-bold uppercase tracking-wide" style={{ background: URGENCY.high.bg, color: URGENCY.high.text, border: '1px solid ' + URGENCY.high.border }}>
                    urgent
                  </span>
                )}
                {email.category && (
                  <span className="text-[8.5px] px-1 py-0.5 rounded" style={{ background: getCat(email.category).bg, color: getCat(email.category).text, border: '1px solid ' + getCat(email.category).border }}>
                    {email.category}
                  </span>
                )}
                {email.matched_subscriber && (
                  <span className="text-[8.5px] px-1 py-0.5 rounded" style={{ background: 'rgba(20,184,166,0.1)', color: '#5EEAD4', border: '1px solid rgba(20,184,166,0.18)' }}>
                    subscriber
                  </span>
                )}
                {email.reply_count > 0 && (
                  <span
                    className="text-[8.5px] px-1 py-0.5 rounded flex items-center gap-0.5"
                    data-testid={'reply-count-badge-' + email.id}
                    style={{ background: 'rgba(201,147,22,0.08)', color: 'rgba(201,147,22,0.65)', border: '1px solid rgba(201,147,22,0.18)', fontFamily: SANS }}
                  >
                    {email.reply_count} {email.reply_count === 1 ? 'reply' : 'replies'}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

/* ─── TCL Mail Agent Panel ──────────────────────────────── */
const TCLMailAgentPanel = ({ detail, onUseAiDraft, onAiRefresh, actionLoading, onStageUpgrade }) => {
  const hasData = detail?.ai_processed || detail?.ai_summary || detail?.ai_category;
  const urg = getUrg(detail?.ai_urgency);
  const cat = getCat(detail?.ai_category || detail?.category);
  const sub = detail?.matched_subscriber;
  const canUpgrade = sub && sub.nurture_stage != null && sub.nurture_stage < 50;

  return (
    <div
      data-testid="tcl-agent-panel"
      className="flex-none overflow-y-auto"
      style={{ background: 'linear-gradient(160deg, #0B2247 0%, #071B3A 100%)', borderBottom: '1px solid rgba(201,147,22,0.18)', maxHeight: '45%', padding: '16px 20px' }}
    >
      {/* Agent header row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {/* Animated badge */}
          <div
            className="flex items-center gap-1.5 px-3 py-1 rounded-full"
            style={{ background: 'rgba(201,147,22,0.15)', border: '1px solid rgba(201,147,22,0.45)', boxShadow: '0 0 12px rgba(201,147,22,0.1)' }}
          >
            <Sparkles size={11} style={{ color: GOLDB }} className="animate-pulse" />
            <span style={{ fontFamily: SANS, color: GOLDB, fontSize: '9px', fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase' }}>
              TCL Mail Agent
            </span>
          </div>
          <span className="hidden sm:block text-[9px] uppercase tracking-[0.18em]" style={{ color: 'rgba(201,147,22,0.4)', fontFamily: SANS }}>AI Analysis Active</span>
        </div>

        {detail && (
          <button
            onClick={() => onAiRefresh(detail.id)}
            disabled={actionLoading === 'ai-refresh'}
            data-testid={'action-ai-refresh-' + detail.id}
            className="flex items-center gap-1 text-[10px] transition-all"
            style={{ color: 'rgba(201,147,22,0.5)', fontFamily: SANS }}
          >
            {actionLoading === 'ai-refresh' ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
            Re-analyse
          </button>
        )}
      </div>

      {/* No email selected */}
      {!detail && (
        <div className="text-center py-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-2.5" style={{ background: 'rgba(201,147,22,0.08)', border: '1px solid rgba(201,147,22,0.18)' }}>
            <Bot size={18} style={{ color: 'rgba(201,147,22,0.45)' }} />
          </div>
          <p className="text-xs" style={{ color: 'rgba(250,248,243,0.35)', fontFamily: SANS }}>
            Select an email to initiate AI analysis
          </p>
        </div>
      )}

      {/* Email selected */}
      {detail && (
        <div className="space-y-2.5">
          {/* Category + urgency tags */}
          <div className="flex flex-wrap items-center gap-2">
            {(detail.ai_category || detail.category) && (
              <span className="text-[10px] px-2.5 py-1 rounded-full font-semibold" style={{ background: cat.bg, color: cat.text, border: '1px solid ' + cat.border }}>
                {detail.ai_category || detail.category}
              </span>
            )}
            {detail.ai_urgency && (
              <span className="text-[10px] px-2.5 py-1 rounded-full font-semibold uppercase tracking-wide" style={{ background: urg.bg, color: urg.text, border: '1px solid ' + urg.border }}>
                {detail.ai_urgency} urgency
              </span>
            )}
          </div>

          {/* No AI data state */}
          {!hasData && (
            <div className="rounded-lg p-3" style={{ background: 'rgba(201,147,22,0.05)', border: '1px solid rgba(201,147,22,0.12)' }}>
              <p className="text-xs italic" style={{ color: 'rgba(250,248,243,0.45)', fontFamily: SANS }}>
                AI analysis pending — click <strong style={{ color: 'rgba(201,147,22,0.75)' }}>Re-analyse</strong> above.
                Ensure <code className="font-mono text-[10px]">EMERGENT_LLM_KEY</code> is set in production secrets.
              </p>
            </div>
          )}

          {/* Executive Summary */}
          {detail.ai_summary && (
            <div>
              <p className="text-[9px] font-semibold uppercase tracking-[0.22em] mb-1" style={{ color: GOLD, fontFamily: SANS }}>Executive Summary</p>
              <p className="text-xs leading-relaxed" style={{ color: 'rgba(250,248,243,0.82)', fontFamily: SANS }}>{detail.ai_summary}</p>
            </div>
          )}

          {/* Detected Intent */}
          {detail.ai_intent && (
            <div>
              <p className="text-[9px] font-semibold uppercase tracking-[0.22em] mb-1" style={{ color: GOLD, fontFamily: SANS }}>Detected Intent</p>
              <span className="inline-block text-[10px] px-2.5 py-1 rounded-full" style={{ background: 'rgba(201,147,22,0.1)', color: 'rgba(250,248,243,0.72)', border: '1px solid rgba(201,147,22,0.22)', fontFamily: SANS }}>
                {detail.ai_intent}
              </span>
            </div>
          )}

          {/* AI Draft Reply */}
          {detail.ai_draft_reply && (
            <div className="rounded-lg overflow-hidden" style={{ border: '1px solid rgba(201,147,22,0.22)', background: 'rgba(4,14,31,0.6)' }}>
              <div className="flex items-center justify-between px-3 py-2" style={{ background: 'rgba(201,147,22,0.07)', borderBottom: '1px solid rgba(201,147,22,0.12)' }}>
                <p className="text-[9px] font-semibold uppercase tracking-[0.22em]" style={{ color: GOLD, fontFamily: SANS }}>AI Draft Reply</p>
                <button
                  onClick={onUseAiDraft}
                  data-testid="use-draft-reply-button"
                  className="flex items-center gap-1.5 px-3 py-1 rounded text-[10px] font-semibold transition-all active:scale-95"
                  style={{ background: GOLD, color: BG, fontFamily: SANS, boxShadow: '0 0 8px rgba(201,147,22,0.35)' }}
                >
                  <Zap size={9} /> Use as Reply
                </button>
              </div>
              <pre className="px-3 py-2.5 text-[11px] leading-relaxed whitespace-pre-wrap" style={{ color: 'rgba(250,248,243,0.6)', fontFamily: SANS, maxHeight: '90px', overflowY: 'auto' }}>
                {detail.ai_draft_reply}
              </pre>
            </div>
          )}

          {/* Smart AI Routing — stage upgrade suggestion */}
          {canUpgrade && (
            <div className="rounded-lg p-2.5" style={{ background: 'rgba(20,184,166,0.05)', border: '1px solid rgba(20,184,166,0.2)' }}>
              <p className="text-[9px] font-semibold uppercase tracking-[0.22em] mb-1" style={{ color: 'rgba(20,184,166,0.75)', fontFamily: SANS }}>
                Smart Routing
              </p>
              <p className="text-[10px] mb-2 leading-relaxed" style={{ color: 'rgba(250,248,243,0.5)', fontFamily: SANS }}>
                {sub.first_name || sub.email} is at Stage {sub.nurture_stage}. Ready to advance their journey?
              </p>
              <button
                onClick={() => onStageUpgrade(detail.from_email, sub.nurture_stage + 1)}
                data-testid="ai-stage-upgrade-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold w-full justify-center transition-all active:scale-95"
                style={{ background: 'rgba(20,184,166,0.12)', border: '1px solid rgba(20,184,166,0.3)', color: '#99F6E4', fontFamily: SANS, boxShadow: '0 0 8px rgba(20,184,166,0.12)' }}
              >
                <Zap size={9} /> Advance to Stage {sub.nurture_stage + 1}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── SubscriberProfileModal ─────────────────────────────── */
const STAGE_MAP = { 0: 'New', 1: 'Engaged', 2: 'Qualified', 3: 'Nurtured', 4: 'Converted' };

const SubscriberProfileModal = ({ email, token, onComposeTo, onOpenEmail, onClose }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!email) return;
    setLoading(true); setError('');
    fetch(API_URL + '/api/compose/subscriber/' + encodeURIComponent(email), {
      headers: { Authorization: 'Bearer ' + token }, credentials: 'include',
    })
      .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e.detail)))
      .then(d => { setProfile(d); setLoading(false); })
      .catch(e => { setError(String(e)); setLoading(false); });
  }, [email, token]);

  const sub = profile?.subscriber;
  const name = sub ? [sub.first_name, sub.last_name].filter(Boolean).join(' ') : email;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(4,14,31,0.82)', backdropFilter: 'blur(6px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div
        data-testid="subscriber-profile-modal"
        className="relative flex flex-col rounded-2xl overflow-hidden"
        style={{ width: '640px', maxWidth: '95vw', maxHeight: '88vh', background: BG, border: '1px solid rgba(201,147,22,0.25)', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(201,147,22,0.15)', background: BG2 }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-base font-bold" style={{ background: sub?.confirmed ? 'rgba(20,184,166,0.18)' : 'rgba(100,116,139,0.18)', color: sub?.confirmed ? '#99F6E4' : '#94A3B8', fontFamily: SERIF }}>
              {name[0]?.toUpperCase()}
            </div>
            <div>
              <h3 style={{ fontFamily: SERIF, color: CREAM, fontSize: '18px', fontWeight: 600 }}>{name}</h3>
              <p className="text-[11px]" style={{ color: 'rgba(250,248,243,0.4)', fontFamily: SANS }}>{email}</p>
            </div>
            {sub?.confirmed && <span className="text-[9px] px-2 py-0.5 rounded-full ml-1" style={{ background: 'rgba(20,184,166,0.12)', color: '#99F6E4', border: '1px solid rgba(20,184,166,0.25)', fontFamily: SANS }}>✓ Confirmed</span>}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { onComposeTo({ to_email: email, to_name: name, from_alias: 'hello@thirdcultureleadership.co.uk' }); onClose(); }}
              data-testid="profile-compose-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
              style={{ background: GOLD, color: BG, fontFamily: SANS }}
            >
              <PenSquare size={12} /> Email
            </button>
            <button onClick={onClose} style={{ color: 'rgba(250,248,243,0.35)' }}><X size={18} /></button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && <div className="flex items-center justify-center py-16"><Loader2 size={22} className="animate-spin" style={{ color: 'rgba(201,147,22,0.4)' }} /></div>}
          {error && <p className="text-xs text-center py-8" style={{ color: '#FCA5A5', fontFamily: SANS }}>{error}</p>}

          {sub && (
            <div className="space-y-6">
              {/* Profile grid */}
              <div>
                <p className="text-[9px] font-semibold uppercase tracking-[0.22em] mb-3" style={{ color: GOLD, fontFamily: SANS }}>Subscriber Profile</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {[
                    ['Nurture Stage', STAGE_MAP[sub.nurture_stage] != null ? STAGE_MAP[sub.nurture_stage] + ' (Stage ' + sub.nurture_stage + ')' : '—'],
                    ['Joined', sub.created_at ? new Date(sub.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'],
                    ['Confirmed', sub.confirmed ? 'Yes · ' + (sub.confirmed_at ? new Date(sub.confirmed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '') : 'No'],
                    ['Source', sub.source || '—'],
                    ['Nurture Paused', sub.nurture_paused ? 'Yes' : 'No'],
                    ['Email Failures', String(sub.email_failures || 0)],
                  ].map(([l, v]) => (
                    <div key={l} className="rounded-lg p-3" style={{ background: 'rgba(250,248,243,0.03)', border: '1px solid rgba(250,248,243,0.07)' }}>
                      <p className="text-[9px] uppercase tracking-wide mb-1" style={{ color: 'rgba(250,248,243,0.28)', fontFamily: SANS }}>{l}</p>
                      <p className="text-xs font-medium" style={{ color: CREAM, fontFamily: SANS }}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Inbound email history */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[9px] font-semibold uppercase tracking-[0.22em]" style={{ color: GOLD, fontFamily: SANS }}>
                    Emails Received from Subscriber ({profile.total_received})
                  </p>
                </div>
                {profile.email_history.length === 0 ? (
                  <p className="text-xs" style={{ color: 'rgba(250,248,243,0.25)', fontFamily: SANS }}>No inbound emails on record</p>
                ) : (
                  <div className="space-y-2">
                    {profile.email_history.map(e => {
                      const urg = getUrg(e.ai_urgency);
                      return (
                        <button
                          key={e.id}
                          onClick={() => { onOpenEmail(e.id); onClose(); }}
                          data-testid={'profile-email-link-' + e.id}
                          className="w-full text-left rounded-lg px-4 py-3 transition-all"
                          style={{ background: 'rgba(250,248,243,0.03)', border: '1px solid rgba(250,248,243,0.07)' }}
                          onMouseEnter={ev => ev.currentTarget.style.background = 'rgba(201,147,22,0.06)'}
                          onMouseLeave={ev => ev.currentTarget.style.background = 'rgba(250,248,243,0.03)'}
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="text-xs font-medium truncate" style={{ color: CREAM, fontFamily: SANS }}>{e.subject || '(no subject)'}</span>
                            <span className="text-[10px] font-mono flex-shrink-0" style={{ color: 'rgba(250,248,243,0.28)' }}>{fmtDate(e.received_at)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {e.ai_urgency && e.ai_urgency !== 'low' && <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: urg.bg, color: urg.text, border: '1px solid ' + urg.border }}>{e.ai_urgency}</span>}
                            {e.category && <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: getCat(e.category).bg, color: getCat(e.category).text }}>{e.category}</span>}
                            {e.ai_summary && <span className="text-[10px] truncate" style={{ color: 'rgba(250,248,243,0.3)', fontFamily: SANS, fontStyle: 'italic' }}>{e.ai_summary}</span>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Sent email history */}
              {profile.sent_history.length > 0 && (
                <div>
                  <p className="text-[9px] font-semibold uppercase tracking-[0.22em] mb-3" style={{ color: 'rgba(201,147,22,0.6)', fontFamily: SANS }}>
                    Emails Sent TO Subscriber ({profile.total_sent})
                  </p>
                  <div className="space-y-1.5">
                    {profile.sent_history.map((e, i) => (
                      <div key={i} className="flex items-center gap-3 rounded-lg px-4 py-2.5" style={{ background: 'rgba(250,248,243,0.02)', border: '1px solid rgba(250,248,243,0.06)' }}>
                        <SendHorizonal size={11} style={{ color: 'rgba(201,147,22,0.4)', flexShrink: 0 }} />
                        <span className="flex-1 text-xs truncate" style={{ color: 'rgba(250,248,243,0.55)', fontFamily: SANS }}>{e.subject || '(no subject)'}</span>
                        <span className="text-[10px] font-mono flex-shrink-0" style={{ color: 'rgba(250,248,243,0.25)' }}>{fmtDate(e.received_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Nurture sequence history */}
              {profile.nurture_history && profile.nurture_history.length > 0 && (
                <div>
                  <p className="text-[9px] font-semibold uppercase tracking-[0.22em] mb-3" style={{ color: 'rgba(94,234,212,0.7)', fontFamily: SANS }}>
                    Nurture Sequence ({profile.total_nurture} emails)
                  </p>
                  <div className="space-y-1.5">
                    {profile.nurture_history.map((e, i) => {
                      const isSent   = e.status === 'sent';
                      const isFailed = e.status === 'failed';
                      return (
                        <div key={i} className="flex items-center gap-3 rounded-lg px-4 py-2.5" style={{ background: 'rgba(94,234,212,0.02)', border: '1px solid rgba(94,234,212,0.08)' }}>
                          <div className="flex-shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded" style={{ background: 'rgba(94,234,212,0.1)', color: '#99F6E4', fontFamily: SANS, minWidth: '36px', textAlign: 'center' }}>
                            #{e.stage}
                          </div>
                          <span className="flex-1 text-xs truncate" style={{ color: 'rgba(250,248,243,0.55)', fontFamily: SANS }}>{e.subject || (e.type === 'reengagement' ? 'Re-engagement email' : 'Stage ' + e.stage + ' nurture email')}</span>
                          <span
                            className="text-[9px] px-1.5 py-0.5 rounded flex-shrink-0"
                            style={{
                              background: isSent ? 'rgba(34,197,94,0.1)' : isFailed ? 'rgba(239,68,68,0.1)' : 'rgba(100,116,139,0.1)',
                              color: isSent ? '#86EFAC' : isFailed ? '#FCA5A5' : '#94A3B8',
                              border: '1px solid ' + (isSent ? 'rgba(34,197,94,0.2)' : isFailed ? 'rgba(239,68,68,0.2)' : 'rgba(100,116,139,0.2)'),
                            }}
                          >{e.status || 'sent'}</span>
                          <span className="text-[10px] font-mono flex-shrink-0" style={{ color: 'rgba(250,248,243,0.22)' }}>{fmtDate(e.timestamp)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── ThreadItem ─────────────────────────────────────────── */
const ThreadItem = ({ type, senderName, senderEmail, recipientEmail, body, bodyHtml, time, showHtml, alias, isLast }) => {
  const [expanded, setExpanded] = useState(isLast);
  const isSent = type === 'sent';
  const preview = body ? body.slice(0, 180) + (body.length > 180 ? '…' : '') : '(no content)';
  const ini = isSent ? 'PM' : (senderName ? senderName[0].toUpperCase() : (senderEmail || '?')[0].toUpperCase());

  return (
    <div
      className="relative"
      style={{ borderLeft: '2px solid ' + (isSent ? 'rgba(201,147,22,0.35)' : 'rgba(59,130,246,0.35)'), paddingLeft: '16px', marginLeft: '8px' }}
    >
      {/* Timeline dot */}
      <div
        className="absolute w-3 h-3 rounded-full"
        style={{ left: '-7px', top: '14px', background: isSent ? GOLD : '#3B82F6', boxShadow: '0 0 6px ' + (isSent ? 'rgba(201,147,22,0.5)' : 'rgba(59,130,246,0.4)') }}
      />

      <div className="rounded-xl overflow-hidden mb-4" style={{ background: isSent ? 'rgba(201,147,22,0.06)' : 'rgba(9,32,66,0.6)', border: '1px solid ' + (isSent ? 'rgba(201,147,22,0.2)' : 'rgba(59,130,246,0.15)') }}>
        {/* Thread item header */}
        <button
          onClick={() => setExpanded(e => !e)}
          className="w-full flex items-center gap-3 px-4 py-3 text-left"
          style={{ borderBottom: expanded ? ('1px solid ' + (isSent ? 'rgba(201,147,22,0.12)' : 'rgba(59,130,246,0.12)')) : 'none' }}
        >
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: isSent ? 'rgba(201,147,22,0.2)' : 'rgba(59,130,246,0.15)', color: isSent ? GOLDB : '#93C5FD', fontFamily: SERIF }}>
            {ini}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold" style={{ color: isSent ? GOLDB : CREAM, fontFamily: SANS }}>
                {isSent ? ('Promise Monday' + (alias ? ' via ' + alias.split('@')[0] + '@' : '')) : (senderName || senderEmail)}
              </span>
              {isSent
                ? <span className="text-[10px]" style={{ color: 'rgba(250,248,243,0.3)', fontFamily: SANS }}>→ {recipientEmail}</span>
                : <span className="text-[10px]" style={{ color: 'rgba(250,248,243,0.3)', fontFamily: SANS }}>&lt;{senderEmail}&gt;</span>}
            </div>
            {!expanded && <p className="text-[10px] truncate mt-0.5" style={{ color: 'rgba(250,248,243,0.35)', fontFamily: SANS }}>{preview}</p>}
          </div>
          <span className="text-[10px] font-mono flex-shrink-0" style={{ color: 'rgba(250,248,243,0.3)' }}>{fmtDate(time)}</span>
        </button>

        {/* Expanded body */}
        {expanded && (
          <div className="px-5 py-4">
            {showHtml && bodyHtml ? (
              <div className="text-sm leading-relaxed" style={{ color: 'rgba(250,248,243,0.75)' }} dangerouslySetInnerHTML={{ __html: bodyHtml }} />
            ) : (
              <pre className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: 'rgba(250,248,243,0.75)', fontFamily: SANS }}>
                {body || '(no content)'}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── EmailBodyPanel (Thread View) ──────────────────────── */
const EmailBodyPanel = ({ detail, detailLoading, showHtml, setShowHtml, onSubscriberClick, onOpenEmail, token }) => {
  // ── Lightbox state ──────────────────────────────────────
  const [lbAtts, setLbAtts]     = useState(null);   // array of image attachments shown in lightbox
  const [lbIdx, setLbIdx]       = useState(0);
  const [lbUrls, setLbUrls]     = useState({});      // attachment_id → object URL

  // Reset lightbox when switching emails
  useEffect(() => { setLbAtts(null); setLbUrls({}); }, [detail?.id]);

  // ESC key closes lightbox
  useEffect(() => {
    if (!lbAtts) return;
    const handler = e => { if (e.key === 'Escape') setLbAtts(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lbAtts]);

  const fetchLbUrl = async (att) => {
    if (lbUrls[att.attachment_id]) return lbUrls[att.attachment_id];
    try {
      const r = await fetch(
        `${API_URL}/api/hostinger-email/email/${detail.id}/attachment/${att.attachment_id}`,
        { headers: { Authorization: 'Bearer ' + token }, credentials: 'include' }
      );
      if (!r.ok) return null;
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      setLbUrls(prev => ({ ...prev, [att.attachment_id]: url }));
      return url;
    } catch { return null; }
  };

  const downloadAttachment = async (emailId, att) => {
    if (!att.attachment_id) {
      toast.error('Download unavailable — older email has no attachment ID');
      return;
    }
    try {
      const r = await fetch(`${API_URL}/api/hostinger-email/email/${emailId}/attachment/${att.attachment_id}`, {
        headers: { Authorization: 'Bearer ' + token },
        credentials: 'include',
      });
      if (!r.ok) { toast.error('Attachment download failed'); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename || 'attachment';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Downloading ' + (att.filename || 'attachment'));
    } catch { toast.error('Attachment download failed'); }
  };

  const handleAttachmentClick = (att) => {
    if (!att.attachment_id) { toast.error('No attachment ID — older email'); return; }
    const isImg = (att.mime_type || '').startsWith('image/');
    if (isImg) {
      const imgAtts = (detail.attachments || []).filter(a => (a.mime_type || '').startsWith('image/') && a.attachment_id);
      const idx = Math.max(0, imgAtts.findIndex(a => a.attachment_id === att.attachment_id));
      setLbAtts(imgAtts);
      setLbIdx(idx);
      fetchLbUrl(att); // async — will update lbUrls and trigger re-render
    } else {
      downloadAttachment(detail.id, att);
    }
  };

  // Pre-fetch when lightbox index changes
  useEffect(() => {
    if (!lbAtts || !lbAtts[lbIdx]) return;
    fetchLbUrl(lbAtts[lbIdx]); // eslint-disable-line react-hooks/exhaustive-deps
  }, [lbIdx, lbAtts]); // eslint-disable-line react-hooks/exhaustive-deps

  const lbCurrent = lbAtts?.[lbIdx];
  const lbUrl     = lbCurrent ? lbUrls[lbCurrent.attachment_id] : null;
  if (detailLoading) {
    return (
      <div className="flex-1 flex items-center justify-center" style={{ background: BG3 }}>
        <Loader2 size={22} style={{ color: 'rgba(201,147,22,0.4)' }} className="animate-spin" />
      </div>
    );
  }
  if (!detail) return null;

  const cat = getCat(detail.category);
  const urg = getUrg(detail.ai_urgency);
  const isSent = detail.direction === 'sent';

  // Build thread items: original + all replies, chronological
  const replies = detail.replies_sent || [];
  const threadItems = [
    {
      type:           isSent ? 'sent' : 'received',
      senderName:     isSent ? 'Promise Monday' : detail.from_name,
      senderEmail:    isSent ? (detail.from_email || TARGET_MAILBOX) : detail.from_email,
      recipientEmail: isSent ? (Array.isArray(detail.to) ? detail.to[0] : (detail.to || '')) : detail.original_recipient,
      body:           detail.body_text,
      bodyHtml:       detail.body_html,
      time:           detail.received_at,
      alias:          isSent ? detail.from_email : null,
    },
    ...replies.map(r => ({
      type:           'sent',
      senderName:     'Promise Monday',
      senderEmail:    detail.original_recipient || TARGET_MAILBOX,
      recipientEmail: r.to,
      body:           r.body_text,
      bodyHtml:       '',
      time:           r.sent_at,
      alias:          detail.original_recipient,
    })),
  ].sort((a, b) => new Date(a.time) - new Date(b.time));

  const hasThread = threadItems.length > 1;

  return (
    <div className="flex-1 overflow-y-auto" style={{ background: BG3 }}>
      <div className="p-5">
        {/* Email header */}
        <div className="mb-5 pb-4" style={{ borderBottom: '1px solid rgba(201,147,22,0.1)' }}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <h2 className="text-xl font-semibold leading-snug flex-1" style={{ fontFamily: SERIF, color: CREAM, letterSpacing: '-0.01em' }}>
              {detail.subject}
            </h2>
            <div className="flex-shrink-0 flex flex-col items-end gap-1">
              {detail.ai_urgency && (
                <span className="text-[9px] px-2 py-0.5 rounded font-semibold uppercase tracking-wider" style={{ background: urg.bg, color: urg.text, border: '1px solid ' + urg.border }}>
                  {detail.ai_urgency}
                </span>
              )}
              {detail.category && (
                <span className="text-[9px] px-2 py-0.5 rounded font-medium" style={{ background: cat.bg, color: cat.text, border: '1px solid ' + cat.border }}>
                  {detail.category}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-1 text-[11px]" style={{ fontFamily: SANS }}>
            <div className="flex items-center gap-2">
              <span className="w-7 uppercase tracking-wide text-[9px]" style={{ color: 'rgba(250,248,243,0.28)' }}>From</span>
              <span style={{ color: 'rgba(250,248,243,0.72)' }}>{detail.from_name ? detail.from_name + ' <' + detail.from_email + '>' : detail.from_email}</span>
            </div>
            {detail.original_recipient && (
              <div className="flex items-center gap-2">
                <span className="w-7 uppercase tracking-wide text-[9px]" style={{ color: 'rgba(250,248,243,0.28)' }}>To</span>
                <span className="font-mono" style={{ color: 'rgba(201,147,22,0.7)' }}>{detail.original_recipient}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="w-7 uppercase tracking-wide text-[9px]" style={{ color: 'rgba(250,248,243,0.28)' }}>Date</span>
              <span className="font-mono text-[10px]" style={{ color: 'rgba(250,248,243,0.42)' }}>{fmtFull(detail.received_at)}</span>
            </div>
          </div>

          <div className="flex items-center gap-3 mt-3">
            {detail.body_html && (
              <button onClick={() => setShowHtml(!showHtml)} className="text-[10px] transition-all" style={{ color: 'rgba(201,147,22,0.5)', fontFamily: SANS }}>
                {showHtml ? '← Plain text' : 'View HTML →'}
              </button>
            )}
            {hasThread && (
              <span className="text-[10px]" style={{ color: 'rgba(250,248,243,0.2)', fontFamily: SANS }}>
                {threadItems.length} messages in thread
              </span>
            )}
          </div>
        </div>

        {/* Attachment chips — received email attachments (metadata) */}
        {detail.attachments && detail.attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {detail.attachments.map((att, i) => {
              const isImg = (att.mime_type || '').startsWith('image/');
              return (
                <div key={i}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs"
                  style={{ background: isImg ? 'rgba(147,51,234,0.07)' : 'rgba(59,130,246,0.07)', border: '1px solid ' + (isImg ? 'rgba(147,51,234,0.18)' : 'rgba(59,130,246,0.18)'), color: isImg ? 'rgba(196,181,253,0.85)' : 'rgba(147,197,253,0.85)' }}
                >
                  {isImg ? <ImageIcon size={10} /> : <FileText size={10} />}
                  <span className="truncate max-w-[160px]">{att.filename || 'attachment'}</span>
                  {att.size > 0 && (
                    <span className="opacity-50 text-[9px]">({(att.size / 1024).toFixed(0)} KB)</span>
                  )}
                  <button
                    onClick={() => handleAttachmentClick(att)}
                    data-testid={`download-attachment-${i}`}
                    title={att.attachment_id ? (isImg ? 'Preview ' : 'Download ') + (att.filename || 'attachment') : 'Not available for older emails'}
                    className="ml-0.5 transition-opacity"
                    style={{
                      opacity: att.attachment_id ? 0.7 : 0.25,
                      cursor: att.attachment_id ? 'pointer' : 'not-allowed',
                      color: isImg ? 'rgba(196,181,253,0.9)' : 'rgba(147,197,253,0.9)',
                    }}
                  >
                    {isImg ? <Eye size={10} /> : <Download size={10} />}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Subscriber match card — clickable for profile */}
        {detail.matched_subscriber && (
          <button
            onClick={() => onSubscriberClick(detail.from_email)}
            data-testid="matched-subscriber-card"
            className="w-full text-left rounded-xl p-4 mb-4 transition-all"
            style={{ background: 'rgba(20,184,166,0.06)', border: '1px solid rgba(20,184,166,0.22)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(20,184,166,0.1)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(20,184,166,0.06)'}
          >
            <div className="flex items-center gap-2 mb-2">
              <User size={11} style={{ color: '#99F6E4' }} />
              <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#99F6E4', fontFamily: SANS }}>
                Matched Subscriber — click to view full profile
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[['Name', detail.matched_subscriber.first_name], ['Stage', detail.matched_subscriber.nurture_stage != null ? 'Stage ' + detail.matched_subscriber.nurture_stage : '—'], ['Paused', detail.matched_subscriber.nurture_paused ? 'Yes' : 'No']].map(([l, v]) => (
                <div key={l}>
                  <p className="text-[9px] uppercase tracking-wide mb-0.5" style={{ color: 'rgba(250,248,243,0.28)', fontFamily: SANS }}>{l}</p>
                  <p className="text-xs" style={{ color: 'rgba(250,248,243,0.65)', fontFamily: SANS }}>{v || '—'}</p>
                </div>
              ))}
            </div>
          </button>
        )}

        {/* ── Thread timeline ── */}
        <div className="space-y-0">
          {threadItems.map((item, i) => (
            <ThreadItem
              key={i}
              {...item}
              showHtml={showHtml}
              isLast={i === threadItems.length - 1}
            />
          ))}
        </div>

        {/* Internal notes */}
        {detail.internal_notes && detail.internal_notes.length > 0 && (
          <div className="mt-5 space-y-2">
            <p className="text-[9px] font-semibold uppercase tracking-[0.22em]" style={{ color: 'rgba(245,158,11,0.72)', fontFamily: SANS }}>Internal Notes</p>
            {detail.internal_notes.map(n => (
              <div key={n.id} className="rounded-lg px-3 py-2.5" style={{ background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.15)' }}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px]" style={{ color: 'rgba(245,158,11,0.65)', fontFamily: SANS }}>{n.author}</span>
                  <span className="text-[10px] font-mono" style={{ color: 'rgba(250,248,243,0.25)' }}>{fmtDate(n.created_at)}</span>
                </div>
                <p className="text-xs" style={{ color: 'rgba(250,248,243,0.6)', fontFamily: SANS }}>{n.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Attachment Lightbox ──────────────────────────── */}
      {lbAtts && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          style={{ background: 'rgba(4,14,31,0.93)', backdropFilter: 'blur(8px)' }}
          onClick={() => setLbAtts(null)}
        >
          <div className="relative flex flex-col items-center max-w-full" onClick={e => e.stopPropagation()}>
            {/* Close */}
            <button
              onClick={() => setLbAtts(null)}
              data-testid="lightbox-close-btn"
              className="absolute -top-10 right-0 transition-opacity opacity-50 hover:opacity-100"
              style={{ color: CREAM }}
            >
              <X size={22} />
            </button>

            {/* Image or spinner */}
            <div
              className="flex items-center justify-center rounded-xl overflow-hidden"
              style={{ minWidth: '80px', minHeight: '80px', maxWidth: '85vw', maxHeight: '78vh', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(201,147,22,0.15)', boxShadow: '0 40px 80px rgba(0,0,0,0.7)' }}
            >
              {lbUrl ? (
                <img
                  src={lbUrl}
                  alt={lbCurrent?.filename || 'attachment'}
                  data-testid="lightbox-image"
                  style={{ maxWidth: '85vw', maxHeight: '78vh', objectFit: 'contain', display: 'block' }}
                />
              ) : (
                <div className="p-14">
                  <Loader2 size={28} className="animate-spin" style={{ color: 'rgba(201,147,22,0.5)' }} />
                </div>
              )}
            </div>

            {/* Filename */}
            {lbCurrent && (
              <p className="mt-3 text-xs" style={{ color: 'rgba(250,248,243,0.45)', fontFamily: SANS }}>
                {lbCurrent.filename}{lbCurrent.size > 0 && ` · ${(lbCurrent.size / 1024).toFixed(0)} KB`}
              </p>
            )}

            {/* Multi-image navigation */}
            {lbAtts.length > 1 && (
              <div className="flex items-center gap-5 mt-3">
                <button
                  onClick={() => setLbIdx(i => Math.max(0, i - 1))}
                  disabled={lbIdx === 0}
                  data-testid="lightbox-prev-btn"
                  className="disabled:opacity-20 transition-opacity"
                  style={{ color: 'rgba(250,248,243,0.65)' }}
                >
                  <ChevronLeft size={22} />
                </button>
                <span className="text-xs font-mono" style={{ color: 'rgba(250,248,243,0.3)', fontFamily: SANS }}>
                  {lbIdx + 1} / {lbAtts.length}
                </span>
                <button
                  onClick={() => setLbIdx(i => Math.min(lbAtts.length - 1, i + 1))}
                  disabled={lbIdx === lbAtts.length - 1}
                  data-testid="lightbox-next-btn"
                  className="disabled:opacity-20 transition-opacity"
                  style={{ color: 'rgba(250,248,243,0.65)' }}
                >
                  <ChevronRight size={22} />
                </button>
              </div>
            )}

            {/* Download current image */}
            {lbCurrent && (
              <button
                onClick={() => downloadAttachment(detail.id, lbCurrent)}
                data-testid="lightbox-download-btn"
                className="mt-3 flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs transition-all"
                style={{ background: 'rgba(201,147,22,0.1)', border: '1px solid rgba(201,147,22,0.25)', color: GOLDB, fontFamily: SANS }}
              >
                <Download size={11} /> Download
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};





/* ─── DeliveryEventsPanel ────────────────────────────────── */
const EV_STYLES = {
  opened:     { bg: 'rgba(16,185,129,0.12)',  text: '#6EE7B7', border: 'rgba(16,185,129,0.28)',  dot: '#10B981' },
  clicked:    { bg: 'rgba(59,130,246,0.12)',  text: '#93C5FD', border: 'rgba(59,130,246,0.28)',  dot: '#3B82F6' },
  bounced:    { bg: 'rgba(239,68,68,0.12)',   text: '#FCA5A5', border: 'rgba(239,68,68,0.28)',   dot: '#EF4444' },
  complained: { bg: 'rgba(245,158,11,0.12)',  text: '#FCD34D', border: 'rgba(245,158,11,0.28)',  dot: '#F59E0B' },
  delivered:  { bg: 'rgba(100,116,139,0.1)',  text: '#CBD5E1', border: 'rgba(100,116,139,0.22)', dot: '#94A3B8' },
  delayed:    { bg: 'rgba(100,116,139,0.1)',  text: '#CBD5E1', border: 'rgba(100,116,139,0.22)', dot: '#94A3B8' },
};
const getEvStyle = t => EV_STYLES[t] || EV_STYLES.delivered;
const EV_TYPES = ['', 'opened', 'clicked', 'bounced', 'complained', 'delivered'];
const EV_PAGE = 50;

const DeliveryEventsPanel = ({ token }) => {
  const [events, setEvents]       = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(false);
  const [page, setPage]           = useState(1);
  const [typeFilter, setTypeFilter] = useState('');

  const loadEvents = useCallback(async (pg, type) => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page: pg, page_size: EV_PAGE });
      if (type) p.set('event_type', type);
      const r = await fetch(`${API_URL}/api/cfq/nurture/admin/email-events-feed?${p}`, {
        headers: { Authorization: 'Bearer ' + token }, credentials: 'include',
      });
      if (r.ok) { const d = await r.json(); setEvents(d.events || []); setTotal(d.total || 0); }
    } catch {}
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { loadEvents(page, typeFilter); }, [page, typeFilter, loadEvents]);

  const totalPages = Math.ceil(total / EV_PAGE);

  return (
    <div className="flex flex-col h-full" data-testid="delivery-events-panel">
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between flex-shrink-0" style={{ borderBottom: '1px solid rgba(201,147,22,0.1)' }}>
        <div className="flex items-center gap-2">
          <Activity size={13} style={{ color: GOLD }} />
          <span style={{ fontFamily: SERIF, color: CREAM, fontSize: '13px', fontWeight: 600 }}>Delivery Events</span>
          {total > 0 && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(201,147,22,0.1)', color: GOLDB, border: '1px solid rgba(201,147,22,0.2)', fontFamily: SANS }}>{total}</span>
          )}
        </div>
        <button onClick={() => loadEvents(page, typeFilter)} style={{ color: 'rgba(250,248,243,0.3)' }} title="Refresh">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Event type pills */}
      <div className="px-3 py-2 flex items-center gap-1.5 flex-shrink-0 flex-wrap" style={{ borderBottom: '1px solid rgba(201,147,22,0.07)' }}>
        {EV_TYPES.map(t => {
          const es = t ? getEvStyle(t) : null;
          const active = typeFilter === t;
          return (
            <button
              key={t || 'all'}
              onClick={() => { setTypeFilter(t); setPage(1); }}
              data-testid={'events-filter-' + (t || 'all')}
              className="text-[9.5px] px-2.5 py-0.5 rounded-full transition-all capitalize"
              style={{
                background: active ? (es?.bg || 'rgba(201,147,22,0.12)') : 'transparent',
                border: '1px solid ' + (active ? (es?.border || 'rgba(201,147,22,0.3)') : 'rgba(250,248,243,0.1)'),
                color: active ? (es?.text || GOLDB) : 'rgba(250,248,243,0.32)',
                fontFamily: SANS,
              }}
            >
              {t || 'All'}
            </button>
          );
        })}
      </div>

      {/* Events feed */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={18} style={{ color: 'rgba(201,147,22,0.4)' }} className="animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-16 px-6">
            <Activity size={26} style={{ color: 'rgba(250,248,243,0.07)' }} className="mx-auto mb-3" />
            <p className="text-xs mb-1" style={{ color: 'rgba(250,248,243,0.22)', fontFamily: SANS }}>
              {typeFilter ? `No ${typeFilter} events yet` : 'No delivery events recorded'}
            </p>
            <p className="text-[10px] leading-relaxed" style={{ color: 'rgba(250,248,243,0.13)', fontFamily: SANS }}>
              Configure the Resend webhook at<br />
              <code style={{ color: GOLD, fontSize: '9px' }}>/api/cfq/nurture/resend-webhook</code>
              <br />to start tracking opens & bounces
            </p>
          </div>
        ) : events.map((ev, i) => {
          const es = getEvStyle(ev.event_type);
          return (
            <div
              key={ev.id || i}
              data-testid={'event-row-' + i}
              className="flex items-start gap-3 px-4 py-3 transition-colors"
              style={{ borderBottom: '1px solid rgba(201,147,22,0.05)' }}
            >
              {/* Event type dot */}
              <div className="flex-shrink-0 mt-1.5">
                <div className="w-2 h-2 rounded-full" style={{ background: es.dot, boxShadow: '0 0 5px ' + es.dot + '80' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <span
                    className="text-[10px] px-2 py-0.5 rounded-full font-medium capitalize"
                    style={{ background: es.bg, color: es.text, border: '1px solid ' + es.border, fontFamily: SANS }}
                  >
                    {ev.event_type || 'unknown'}
                  </span>
                  {ev.stage != null && (
                    <span className="text-[9px]" style={{ color: 'rgba(201,147,22,0.5)', fontFamily: SANS }}>Stage {ev.stage}</span>
                  )}
                  <span className="text-[9px] font-mono ml-auto flex-shrink-0" style={{ color: 'rgba(250,248,243,0.22)' }}>
                    {fmtDate(ev.timestamp)}
                  </span>
                </div>
                <p className="text-xs truncate" style={{ color: 'rgba(250,248,243,0.58)', fontFamily: SANS }}>
                  {ev.subscriber_email || '—'}
                </p>
                {ev.click_url && (
                  <p className="text-[10px] truncate mt-0.5" style={{ color: 'rgba(59,130,246,0.5)', fontFamily: SANS }}>
                    → {ev.click_url}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-2.5 flex-shrink-0" style={{ borderTop: '1px solid rgba(201,147,22,0.08)', fontFamily: SANS, fontSize: '10px', color: 'rgba(250,248,243,0.28)' }}>
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="disabled:opacity-30">← Prev</button>
          <span className="font-mono">{page} / {totalPages} · {total} total</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="disabled:opacity-30">Next →</button>
        </div>
      )}
    </div>
  );
};


const ReplyActionsBar = ({ detail, onAction, onReply, onNote, onCompose, actionLoading, replyText, setReplyText }) => {
  const [showNote, setShowNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const replyAttachRef = useRef(null);

  useEffect(() => { setShowNote(false); setNoteText(''); setReplyAttachments([]); setConfirmDelete(false); }, [detail?.id]);

  if (!detail) return null;

  const handleNote = () => {
    if (!noteText.trim()) return;
    onNote(detail.id, noteText.trim());
    setNoteText(''); setShowNote(false);
  };

  const handleAttachFiles = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) { return; } // skip >10MB
      const reader = new FileReader();
      reader.onload = evt => {
        const base64 = evt.target.result.split(',')[1];
        const isImg = file.type.startsWith('image/');
        setReplyAttachments(prev => [...prev, {
          filename: file.name,
          content: base64,
          size: file.size,
          mime: file.type,
          preview: isImg ? evt.target.result : null, // data URI for image thumbnails
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const handleSendReply = () => {
    onReply(detail.id, replyText, replyAttachments);
    setReplyAttachments([]);
  };

  return (
    <div className="flex-none" style={{ background: BG, borderTop: '1px solid rgba(201,147,22,0.15)' }}>
      {/* Quick action buttons */}
      <div className="flex items-center gap-1.5 px-4 py-2.5 flex-wrap" style={{ borderBottom: '1px solid rgba(201,147,22,0.07)' }}>
        {detail.status !== 'archived' && (
          <ActionBtn icon={Archive} label="Archive" testId={'action-archive-' + detail.id} onClick={() => onAction(detail.id, 'archive')} loading={actionLoading === 'archive'} />
        )}
        {detail.status !== 'spam' && (
          <ActionBtn icon={AlertTriangle} label="Spam" testId={'action-spam-' + detail.id} onClick={() => onAction(detail.id, 'spam')} loading={actionLoading === 'spam'} />
        )}
        {detail.status === 'unread' ? (
          <ActionBtn icon={MailOpen} label="Mark Read" testId={'action-read-' + detail.id} onClick={() => onAction(detail.id, 'read')} loading={actionLoading === 'read'} />
        ) : (
          <ActionBtn icon={Mail} label="Mark Unread" testId={'action-unread-' + detail.id} onClick={() => onAction(detail.id, 'unread')} loading={actionLoading === 'unread'} />
        )}
        {!confirmDelete ? (
          <ActionBtn icon={Trash2} label="Delete" testId={'action-delete-' + detail.id} onClick={() => setConfirmDelete(true)} loading={actionLoading === 'delete'} />
        ) : (
          <div className="flex items-center gap-1.5 ml-0.5">
            <span className="text-[10px]" style={{ color: '#FCA5A5', fontFamily: SANS }}>Delete?</span>
            <button
              onClick={() => { onAction(detail.id, 'delete'); setConfirmDelete(false); }}
              data-testid={'action-delete-confirm-' + detail.id}
              className="text-[10px] px-2.5 py-1 rounded transition-all"
              style={{ background: 'rgba(239,68,68,0.18)', color: '#FCA5A5', border: '1px solid rgba(239,68,68,0.3)', fontFamily: SANS }}
            >Confirm</button>
            <button
              onClick={() => setConfirmDelete(false)}
              data-testid={'action-delete-cancel-' + detail.id}
              className="text-[10px] px-2 py-1 rounded transition-all"
              style={{ color: 'rgba(250,248,243,0.3)', fontFamily: SANS }}
            >Cancel</button>
          </div>
        )}
        <div className="flex-1" />
        <button
          onClick={() => setShowNote(!showNote)}
          data-testid={'add-note-btn-' + detail.id}
          className="flex items-center gap-1 text-[10px] transition-all"
          style={{ color: showNote ? 'rgba(245,158,11,0.75)' : 'rgba(245,158,11,0.42)', fontFamily: SANS }}
        >
          <StickyNote size={11} /> Note
        </button>
      </div>

      {/* Note input */}
      {showNote && (
        <div className="px-4 py-2.5 space-y-2" style={{ borderBottom: '1px solid rgba(201,147,22,0.07)' }}>
          <textarea
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            placeholder="Internal note (not sent to sender)…"
            data-testid={'note-textarea-' + detail.id}
            rows={2}
            className="w-full rounded-lg px-3 py-2 text-xs resize-none focus:outline-none"
            style={{ background: 'rgba(245,158,11,0.04)', border: '1px solid rgba(245,158,11,0.2)', color: 'rgba(250,248,243,0.65)', fontFamily: SANS }}
          />
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setNoteText(''); setShowNote(false); }} className="text-xs" style={{ color: 'rgba(250,248,243,0.3)', fontFamily: SANS }}>Cancel</button>
            <button
              onClick={handleNote}
              disabled={!noteText.trim()}
              data-testid={'save-note-' + detail.id}
              className="text-xs px-3 py-1 rounded transition-all disabled:opacity-40"
              style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.25)', color: '#FCD34D', fontFamily: SANS }}
            >
              Save Note
            </button>
          </div>
        </div>
      )}

      {/* Reply area */}
      <div className="px-4 py-3 space-y-2">
        {/* Hidden file input */}
        <input
          ref={replyAttachRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleAttachFiles}
          accept="*/*"
          data-testid="reply-file-input"
        />

        <textarea
          value={replyText}
          onChange={e => setReplyText(e.target.value)}
          placeholder={'Reply to ' + (detail.from_name || detail.from_email) + '…'}
          data-testid={'reply-textarea-' + detail.id}
          rows={3}
          className="w-full rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none transition-all"
          style={{ background: 'rgba(250,248,243,0.03)', border: '1px solid rgba(201,147,22,0.12)', color: 'rgba(250,248,243,0.78)', fontFamily: SANS }}
          onFocus={e => (e.target.style.borderColor = 'rgba(201,147,22,0.35)')}
          onBlur={e => (e.target.style.borderColor = 'rgba(201,147,22,0.12)')}
        />

        {/* Attachment chips with image thumbnails */}
        {replyAttachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {replyAttachments.map((a, i) => (
              <div key={i} className="flex items-center gap-1.5 rounded-lg overflow-hidden text-xs"
                style={{ background: 'rgba(59,130,246,0.07)', border: '1px solid rgba(59,130,246,0.18)', color: 'rgba(147,197,253,0.85)' }}>
                {a.preview ? (
                  <img src={a.preview} alt={a.filename} className="w-8 h-8 object-cover flex-shrink-0" style={{ borderRight: '1px solid rgba(59,130,246,0.15)' }} />
                ) : (
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ borderRight: '1px solid rgba(59,130,246,0.12)' }}>
                    <FileText size={12} />
                  </div>
                )}
                <span className="truncate max-w-[120px] text-[11px] px-1">{a.filename}</span>
                <span className="text-[9px] opacity-50 pr-1">({(a.size / 1024).toFixed(0)} KB)</span>
                <button
                  type="button"
                  onClick={() => setReplyAttachments(prev => prev.filter((_, j) => j !== i))}
                  className="px-1.5 opacity-50 hover:opacity-100 transition-opacity self-stretch flex items-center"
                  data-testid={`reply-remove-attachment-${i}`}
                >
                  <X size={9} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            onClick={handleSendReply}
            disabled={!replyText.trim() || actionLoading === 'reply'}
            data-testid="send-reply-button"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-40 active:scale-95"
            style={{ background: replyText.trim() ? 'rgba(201,147,22,0.15)' : 'rgba(201,147,22,0.05)', border: '1px solid rgba(201,147,22,0.28)', color: GOLD, fontFamily: SANS }}
          >
            {actionLoading === 'reply' ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            Send Reply
          </button>

          {/* Attach button */}
          <button
            type="button"
            onClick={() => replyAttachRef.current?.click()}
            data-testid="reply-attach-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs transition-all"
            style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.18)', color: 'rgba(147,197,253,0.7)', fontFamily: SANS }}
            title="Attach files to this reply"
          >
            <Paperclip size={12} />
            {replyAttachments.length > 0 ? `${replyAttachments.length} file${replyAttachments.length > 1 ? 's' : ''}` : 'Attach'}
          </button>

          <button
            onClick={() => onCompose({ to_email: detail.reply_to || detail.from_email, to_name: detail.from_name || '', subject: 'Re: ' + (detail.subject || ''), from_alias: detail.original_recipient || 'hello@thirdcultureleadership.co.uk' })}
            data-testid={'compose-from-detail-' + detail.id}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs transition-all"
            style={{ background: 'rgba(201,147,22,0.06)', border: '1px solid rgba(201,147,22,0.15)', color: 'rgba(201,147,22,0.65)', fontFamily: SANS }}
          >
            <PenSquare size={12} /> Rich Reply
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── ActionBtn ─────────────────────────────────────────── */
const ActionBtn = ({ icon: Icon, label, onClick, loading, testId }) => (
  <button
    onClick={onClick}
    disabled={loading}
    data-testid={testId}
    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] transition-all disabled:opacity-40"
    style={{ color: 'rgba(250,248,243,0.4)', border: '1px solid rgba(250,248,243,0.07)', fontFamily: SANS }}
  >
    {loading ? <Loader2 size={11} className="animate-spin" /> : <Icon size={11} />}
    {label}
  </button>
);

/* ─── EmptyEmailState ───────────────────────────────────── */
const EmptyEmailState = () => (
  <div className="flex-1 flex flex-col items-center justify-center text-center px-8" data-testid="empty-email-state" style={{ background: BG3 }}>
    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ background: 'rgba(201,147,22,0.08)', border: '1px solid rgba(201,147,22,0.15)', boxShadow: '0 0 32px rgba(201,147,22,0.07)' }}>
      <Bot size={24} style={{ color: 'rgba(201,147,22,0.5)' }} />
    </div>
    <p className="text-base font-semibold mb-2" style={{ fontFamily: SERIF, color: 'rgba(250,248,243,0.45)' }}>
      Select an email to begin
    </p>
    <p className="text-xs max-w-xs mb-7" style={{ fontFamily: SANS, color: 'rgba(250,248,243,0.22)', lineHeight: 1.7 }}>
      The TCL Mail Agent will analyse the selected email — classifying intent, detecting urgency, and preparing a draft reply.
    </p>
    <blockquote
      className="text-sm italic max-w-xs text-left"
      style={{ fontFamily: SERIF, color: 'rgba(250,248,243,0.28)', borderLeft: '2px solid rgba(201,147,22,0.3)', paddingLeft: '12px' }}
    >
      "Leadership is not about being in charge. It is about taking care of those in your charge."
      <footer className="mt-1.5 not-italic text-[10px]" style={{ color: 'rgba(201,147,22,0.5)', fontFamily: SANS }}>— Dr Promise Monday</footer>
    </blockquote>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════ */
export default function AdminInbox() {
  const { token } = useAuth();

  const [emails, setEmails]           = useState([]);
  const [selectedId, setSelectedId]   = useState(null);
  const [detail, setDetail]           = useState(null);
  const [health, setHealth]           = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [detailLoading, setDetLoading]= useState(false);
  const [activeTab, setActiveTab]     = useState('all');
  const [aliasFilter, setAlias]       = useState('all');
  const [searchQ, setSearchQ]         = useState('');
  const [page, setPage]               = useState(1);
  const [total, setTotal]             = useState(0);
  const [unread, setUnread]           = useState(0);
  const [replied, setReplied]         = useState(0);
  const [actionLoading, setActionLd]  = useState(null);
  const [setupLoading, setSetupLd]    = useState(false);
  const [showMobile, setShowMobile]   = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeData, setComposeData] = useState(null);
  const [replyText, setReplyText]     = useState('');
  const [showHtml, setShowHtml]       = useState(false);
  const [showSubscribers, setShowSubscribers] = useState(false);
  const [syncLoading, setSyncLoading] = useState(null);
  const [subscriberEmail, setSubscriberEmail] = useState(null);

  const PAGE_SIZE = 30;
  const hdrs = useCallback(() => ({ Authorization: 'Bearer ' + token }), [token]);

  // Reset per-email state
  useEffect(() => { setReplyText(''); setShowHtml(false); }, [detail?.id]);

  /* API calls */
  const loadEmails = useCallback(async (tab, alias, pg) => {
    setListLoading(true);
    try {
      const p = new URLSearchParams({ page: pg, page_size: PAGE_SIZE });
      if (tab === 'sent') {
        p.set('direction', 'sent');
      } else if (tab && tab !== 'all') {
        p.set('status', tab);
      }
      if (alias && alias !== 'all') p.set('alias', alias);
      const r = await fetch(API_URL + '/api/hostinger-email/inbox?' + p, { headers: hdrs(), credentials: 'include' });
      if (!r.ok) throw new Error(await r.text());
      const d = await r.json();
      setEmails(d.emails || []);
      setTotal(d.total || 0);
      setUnread(d.unread_count || 0);
      setReplied(d.replied_count || 0);
    } catch (e) { console.error('Inbox fetch', e); }
    finally { setListLoading(false); }
  }, [hdrs]);

  const syncMailbox = async folder => {
    setSyncLoading(folder);
    try {
      const r = await fetch(API_URL + '/api/hostinger-email/sync?folder=' + encodeURIComponent(folder), {
        method: 'POST', headers: { ...hdrs(), 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrf() }, credentials: 'include',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Sync failed');
      toast.success(d.message || 'Sync started. Refresh in a moment.');
      // Refresh inbox after a short delay
      setTimeout(() => loadEmails(activeTab, aliasFilter, page), 5000);
    } catch (e) { toast.error(e.message || 'Sync failed'); }
    finally { setSyncLoading(null); }
  };

  const loadHealth = useCallback(async () => {
    try {
      const r = await fetch(API_URL + '/api/hostinger-email/health', { headers: hdrs(), credentials: 'include' });
      if (r.ok) setHealth(await r.json());
    } catch {}
  }, [hdrs]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeTab !== 'events') loadEmails(activeTab, aliasFilter, page); }, [activeTab, aliasFilter, page]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadHealth(); }, []);

  const selectEmail = async id => {
    setSelectedId(id); setShowMobile(true);
    if (detail?.id === id) return;
    setDetLoading(true);
    try {
      const r = await fetch(API_URL + '/api/hostinger-email/email/' + id, { headers: hdrs(), credentials: 'include' });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setDetail(d);
      // Only decrement unread count if the email was previously unread (not already read)
      const wasUnread = emails.find(e => e.id === id)?.status === 'unread';
      setEmails(prev => prev.map(e => e.id === id && e.status === 'unread' ? { ...e, status: 'read' } : e));
      if (wasUnread) setUnread(prev => Math.max(0, prev - 1));
    } catch { toast.error('Failed to load email'); }
    finally { setDetLoading(false); }
  };

  const handleAction = async (id, action) => {
    setActionLd(action);
    const h = { ...hdrs(), 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrf() };
    try {
      const r = await fetch(API_URL + '/api/hostinger-email/email/' + id + '/action', { method: 'POST', headers: h, credentials: 'include', body: JSON.stringify({ action }) });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      if (action === 'delete') {
        toast.success('Email deleted');
        setDetail(null);
        setSelectedId(null);
      } else {
        const friendlyAction = { archive: 'Archived', spam: 'Marked as spam', unread: 'Marked unread', read: 'Marked as read' };
        toast.success(friendlyAction[action] || ('Done: ' + action));
        setDetail(prev => prev ? { ...prev, status: action === 'archive' ? 'archived' : action } : prev);
        if (action === 'unread') setUnread(prev => prev + 1);
        if (action === 'read') setUnread(prev => Math.max(0, prev - 1));
      }
      loadEmails(activeTab, aliasFilter, page);
    } catch (e) { toast.error(e.message || 'Failed'); }
    finally { setActionLd(null); }
  };

  const handleReply = async (id, text, attachments = []) => {
    if (!text.trim()) { toast.error('Reply cannot be empty'); return; }
    setActionLd('reply');
    const h = { ...hdrs(), 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrf() };
    try {
      const r = await fetch(API_URL + '/api/hostinger-email/email/' + id + '/reply', {
        method: 'POST', headers: h, credentials: 'include',
        body: JSON.stringify({
          body_text: text,
          attachments: attachments.map(a => ({ filename: a.filename, content: a.content })),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success('Reply sent via Hostinger');
      setReplyText('');
      setDetail(prev => prev ? { ...prev, status: 'replied', replies_sent: [...(prev.replies_sent || []), { sent_at: new Date().toISOString(), to: prev.reply_to || prev.from_email, body_text: text, subject: 'Re: ' + prev.subject }] } : prev);
      loadEmails(activeTab, aliasFilter, page);
    } catch (e) { toast.error(e.message || 'Failed'); }
    finally { setActionLd(null); }
  };

  const handleNote = async (id, text) => {
    const h = { ...hdrs(), 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrf() };
    try {
      const r = await fetch(API_URL + '/api/hostinger-email/email/' + id + '/note', { method: 'POST', headers: h, credentials: 'include', body: JSON.stringify({ text }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success('Note saved');
      setDetail(prev => prev ? { ...prev, internal_notes: [...(prev.internal_notes || []), d.note] } : prev);
    } catch (e) { toast.error(e.message || 'Failed'); }
  };

  const handleAiRefresh = async id => {
    setActionLd('ai-refresh');
    const h = { ...hdrs(), 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrf() };
    try {
      const r = await fetch(API_URL + '/api/hostinger-email/email/' + id + '/ai-refresh', { method: 'POST', headers: h, credentials: 'include' });
      if (!r.ok) throw new Error();
      toast.success('AI re-analysis queued — refreshing in 4s…');
      setTimeout(() => selectEmail(id), 4000);
    } catch { toast.error('AI refresh failed'); }
    finally { setActionLd(null); }
  };

  const handleSetup = async () => {
    setSetupLd(true);
    try {
      const r = await fetch(API_URL + '/api/hostinger-email/setup', { method: 'POST', headers: { ...hdrs(), 'Content-Type': 'application/json' }, credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Setup failed');
      toast.success('Mailbox ready: ' + (d.mailbox || d.mailbox_address || ''));
      loadHealth();
    } catch (e) { toast.error(e.message || 'Setup failed'); }
    finally { setSetupLd(false); }
  };

  const useAiDraft = () => {
    if (detail?.ai_draft_reply) { setReplyText(detail.ai_draft_reply); toast.success('AI draft loaded into reply box below'); }
    else toast.error('No AI draft available — click Re-analyse to generate one');
  };

  // ── Bulk stage upgrade ─────────────────────────────────
  const [selectedIds, setSelectedIds]       = useState(new Set());
  const [isBulkUpgrading, setBulkUpgrading] = useState(false);

  const toggleSelect = useCallback(id => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const selectAllMatched = () => {
    const ids = emails
      .filter(e => e.direction !== 'sent' && e.matched_subscriber?.nurture_stage != null)
      .map(e => e.id);
    setSelectedIds(new Set(ids));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkUpgrade = async () => {
    if (selectedIds.size === 0) return;
    setBulkUpgrading(true);
    const toUpgrade = emails.filter(e => selectedIds.has(e.id) && e.matched_subscriber?.nurture_stage != null);
    const h = { ...hdrs(), 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrf() };
    let successCount = 0;
    for (const email of toUpgrade) {
      const sub = email.matched_subscriber;
      const targetStage = sub.nurture_stage + 1;
      const subEmail = sub.email || email.from_email;
      try {
        const r = await fetch(
          `${API_URL}/api/cfq/nurture/admin/subscriber/${encodeURIComponent(subEmail)}/stage`,
          { method: 'PATCH', headers: h, credentials: 'include', body: JSON.stringify({ stage: targetStage }) }
        );
        if (r.ok) successCount++;
      } catch {}
    }
    toast.success(`Advanced ${successCount} of ${toUpgrade.length} subscriber(s) to next stage`);
    setSelectedIds(new Set());
    setBulkUpgrading(false);
    loadEmails(activeTab, aliasFilter, page);
  };

  const handleStageUpgrade = async (subscriberEmail, targetStage) => {
    const h = { ...hdrs(), 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrf() };
    try {
      const r = await fetch(
        `${API_URL}/api/cfq/nurture/admin/subscriber/${encodeURIComponent(subscriberEmail)}/stage`,
        { method: 'PATCH', headers: h, credentials: 'include', body: JSON.stringify({ stage: targetStage }) }
      );
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Stage upgrade failed');
      toast.success(`Subscriber advanced to Stage ${targetStage}`);
      // Update the matched_subscriber nurture_stage in local detail state
      setDetail(prev => prev
        ? { ...prev, matched_subscriber: prev.matched_subscriber ? { ...prev.matched_subscriber, nurture_stage: targetStage } : prev.matched_subscriber }
        : prev
      );
    } catch (e) { toast.error(e.message || 'Stage upgrade failed'); }
  };

  const handleCompose = (prefill = null) => { setComposeData(prefill); setComposeOpen(true); };
  const handleSubscriberClick = email => setSubscriberEmail(email);
  const changeTab   = tab   => { setActiveTab(tab);  setPage(1); setSelectedId(null); setDetail(null); setSelectedIds(new Set()); };
  const changeAlias = alias => { setAlias(alias);    setPage(1); setSelectedId(null); setDetail(null); };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const filtered = searchQ ? emails.filter(e => [(e.from_name || ''), (e.from_email || ''), (e.subject || '')].some(s => s.toLowerCase().includes(searchQ.toLowerCase()))) : emails;

  return (
    <div
      data-testid="admin-inbox-page"
      className="-m-4 sm:-m-6 lg:-m-8 overflow-x-hidden"
      style={{ fontFamily: SANS, background: BG3 }}
    >
      {/* ── Top status bar ── */}
      <StatusHealthBar
        health={health}
        onSetup={handleSetup}
        setupLoading={setupLoading}
        onCompose={() => handleCompose()}
        onRefresh={() => { loadEmails(activeTab, aliasFilter, page); loadHealth(); }}
        refreshing={listLoading}
        unreadCount={unread}
        onSync={syncMailbox}
        syncLoading={syncLoading}
        onShowSubscribers={() => setShowSubscribers(v => !v)}
        showSubscribers={showSubscribers}
      />

      {/* ── Main grid ── */}
      <div
        className="grid overflow-hidden"
        style={{
          height: 'calc(100vh - 62px)',
          gridTemplateColumns: showSubscribers ? '1fr 1fr 380px' : '1fr 1fr',
          gridTemplateRows: '1fr',
        }}
      >
        {/* ── LEFT pane: email list ── */}
        <div
          className={'flex flex-col h-full min-h-0 ' + (showMobile ? 'hidden lg:flex' : 'flex')}
          data-testid="email-list-panel"
          style={{ background: BG, borderRight: '1px solid rgba(201,147,22,0.18)' }}
        >
          {/* Search */}
          <div className="p-3" style={{ borderBottom: '1px solid rgba(201,147,22,0.1)' }}>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: 'rgba(250,248,243,0.04)', border: '1px solid rgba(250,248,243,0.08)' }}>
              <Search size={12} style={{ color: 'rgba(250,248,243,0.28)', flexShrink: 0 }} />
              <input
                type="text"
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                placeholder="Search emails…"
                data-testid="email-search-input"
                className="flex-1 bg-transparent text-xs focus:outline-none"
                style={{ color: CREAM, fontFamily: SANS }}
              />
              {searchQ && (
                <button onClick={() => setSearchQ('')} style={{ color: 'rgba(250,248,243,0.3)' }}>
                  <X size={11} />
                </button>
              )}
            </div>
          </div>

          {/* Status tabs */}
          <div className="flex overflow-x-hidden flex-shrink-0" style={{ borderBottom: '1px solid rgba(201,147,22,0.1)' }}>
            {STATUS_TABS.map(t => (
              <button
                key={t.id}
                onClick={() => changeTab(t.id)}
                data-testid={'inbox-tab-' + t.id}
                className="flex-1 py-2.5 text-[10px] font-medium transition-all truncate"
                style={{ color: activeTab === t.id ? GOLDB : 'rgba(250,248,243,0.32)', borderBottom: '2px solid ' + (activeTab === t.id ? GOLD : 'transparent'), background: activeTab === t.id ? 'rgba(201,147,22,0.04)' : 'transparent', fontFamily: SANS }}
              >
                {t.label}
                {t.id === 'unread' && unread > 0 && <span className="ml-1 text-[9px]" style={{ color: '#99F6E4' }}>({unread})</span>}
                {t.id === 'replied' && replied > 0 && <span className="ml-1 text-[9px]" style={{ color: 'rgba(250,248,243,0.25)' }}>({replied})</span>}
              </button>
            ))}
          </div>

          {/* Alias filter — hide for events tab */}
          {activeTab !== 'events' && (
            <div className="px-3 py-2 flex items-center gap-2" style={{ borderBottom: '1px solid rgba(201,147,22,0.07)' }}>
              <Tag size={11} style={{ color: 'rgba(250,248,243,0.22)', flexShrink: 0 }} />
              <select
                value={aliasFilter}
                onChange={e => changeAlias(e.target.value)}
                data-testid="inbox-alias-filter"
                className="flex-1 bg-transparent text-[11px] focus:outline-none cursor-pointer appearance-none"
                style={{ color: 'rgba(250,248,243,0.48)', fontFamily: SANS }}
              >
                {ALIASES.map(a => <option key={a.value} value={a.value} className="bg-gray-900">{a.label}</option>)}
              </select>
            </div>
          )}

          {/* Bulk select toolbar — shown when matched subscriber emails exist */}
          {activeTab !== 'events' && emails.some(e => e.direction !== 'sent' && e.matched_subscriber?.nurture_stage != null) && (
            <div
              className="px-4 py-1.5 flex items-center gap-2 flex-shrink-0"
              style={{ borderBottom: '1px solid rgba(201,147,22,0.07)', background: selectedIds.size > 0 ? 'rgba(201,147,22,0.04)' : 'transparent' }}
            >
              <span className="text-[9.5px]" style={{ color: 'rgba(250,248,243,0.28)', fontFamily: SANS }}>
                {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Bulk select:'}
              </span>
              {selectedIds.size < emails.filter(e => e.direction !== 'sent' && e.matched_subscriber?.nurture_stage != null).length ? (
                <button
                  onClick={selectAllMatched}
                  data-testid="select-all-matched-btn"
                  className="text-[9.5px] transition-all"
                  style={{ color: GOLD, fontFamily: SANS }}
                >
                  Select all matched ({emails.filter(e => e.direction !== 'sent' && e.matched_subscriber?.nurture_stage != null).length})
                </button>
              ) : (
                <button onClick={clearSelection} className="text-[9.5px]" style={{ color: 'rgba(250,248,243,0.28)', fontFamily: SANS }}>
                  Clear selection
                </button>
              )}
            </div>
          )}

          {/* Email list OR Delivery Events panel */}
          {activeTab === 'events' ? (
            <div className="flex-1 min-h-0 overflow-hidden">
              <DeliveryEventsPanel token={token} />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto" data-testid="email-list-scroll">
              {listLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 size={20} style={{ color: 'rgba(201,147,22,0.4)' }} className="animate-spin" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-16 px-6">
                  <MailOpen size={26} style={{ color: 'rgba(250,248,243,0.1)' }} className="mx-auto mb-3" />
                  <p className="text-xs" style={{ color: 'rgba(250,248,243,0.22)', fontFamily: SANS }}>
                    {searchQ ? 'No matches for "' + searchQ + '"' : (activeTab === 'unread' ? 'No unread emails' : 'No emails in this view')}
                  </p>
                </div>
              ) : (
                filtered.map(e => (
                  <EmailListItem
                    key={e.id}
                    email={e}
                    selected={selectedId === e.id}
                    onClick={selectEmail}
                    isChecked={selectedIds.has(e.id)}
                    onCheckToggle={toggleSelect}
                  />
                ))
              )}
            </div>
          )}

          {/* Pagination — hide for events tab */}
          {activeTab !== 'events' && totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-2.5 flex-shrink-0" style={{ borderTop: '1px solid rgba(201,147,22,0.08)', fontFamily: SANS, fontSize: '10px', color: 'rgba(250,248,243,0.28)' }}>
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="disabled:opacity-30">← Prev</button>
              <span className="font-mono">{page} / {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="disabled:opacity-30">Next →</button>
            </div>
          )}

          {/* Floating bulk-upgrade action bar */}
          {selectedIds.size > 0 && (
            <div
              className="flex-shrink-0 flex items-center justify-between gap-3 px-4 py-3"
              data-testid="bulk-upgrade-bar"
              style={{ background: 'rgba(7,27,58,0.97)', borderTop: '1px solid rgba(201,147,22,0.35)', boxShadow: '0 -6px 24px rgba(0,0,0,0.55)' }}
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ background: GOLDB, boxShadow: '0 0 6px rgba(240,198,84,0.6)' }} />
                <span className="text-xs" style={{ color: CREAM, fontFamily: SANS }}>
                  <strong style={{ color: GOLDB }}>{selectedIds.size}</strong> subscriber{selectedIds.size !== 1 ? 's' : ''} selected
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={clearSelection} className="text-[10px]" style={{ color: 'rgba(250,248,243,0.32)', fontFamily: SANS }}>
                  Clear
                </button>
                <button
                  onClick={handleBulkUpgrade}
                  disabled={isBulkUpgrading}
                  data-testid="bulk-upgrade-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all disabled:opacity-50 active:scale-95"
                  style={{ background: GOLD, color: BG, fontFamily: SANS, boxShadow: '0 0 14px rgba(201,147,22,0.35)' }}
                >
                  {isBulkUpgrading ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />}
                  Advance Stage
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── RIGHT pane: agent + body + reply ── */}
        <div
          className={'flex flex-col h-full min-h-0 overflow-hidden ' + (showMobile ? 'flex' : 'hidden lg:flex')}
          style={{ background: BG3 }}
        >
          {/* Mobile back */}
          {showMobile && (
            <button
              onClick={() => { setShowMobile(false); setSelectedId(null); setDetail(null); }}
              className="lg:hidden flex items-center gap-1.5 px-4 py-3 text-xs flex-shrink-0 transition-all"
              style={{ color: 'rgba(250,248,243,0.4)', borderBottom: '1px solid rgba(201,147,22,0.1)', fontFamily: SANS }}
            >
              <ArrowLeft size={13} /> Back to inbox
            </button>
          )}

          {/* TCL Mail Agent — always visible */}
          <TCLMailAgentPanel
            detail={detailLoading ? null : detail}
            onUseAiDraft={useAiDraft}
            onAiRefresh={handleAiRefresh}
            actionLoading={actionLoading}
            onStageUpgrade={handleStageUpgrade}
          />

          {/* Gold gradient divider */}
          <div className="flex-shrink-0 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(201,147,22,0.4), transparent)' }} />

          {/* Email body or empty state */}
          {!detail && !detailLoading ? (
            <EmptyEmailState />
          ) : (
            <EmailBodyPanel
              detail={detailLoading ? null : detail}
              detailLoading={detailLoading}
              showHtml={showHtml}
              setShowHtml={setShowHtml}
              onSubscriberClick={handleSubscriberClick}
              token={token}
            />
          )}

          {/* Reply actions bar — hide for sent items, show for received */}
          {detail && !detailLoading && detail.direction !== 'sent' && (
            <ReplyActionsBar
              detail={detail}
              onAction={handleAction}
              onReply={handleReply}
              onNote={handleNote}
              onCompose={handleCompose}
              actionLoading={actionLoading}
              replyText={replyText}
              setReplyText={setReplyText}
            />
          )}

          {/* Sent item — just show a quick "Compose follow-up" bar */}
          {detail && !detailLoading && detail.direction === 'sent' && (
            <div
              className="flex-none flex items-center gap-3 px-5 py-3"
              style={{ background: BG, borderTop: '1px solid rgba(201,147,22,0.12)' }}
            >
              <span className="text-xs" style={{ color: 'rgba(250,248,243,0.35)', fontFamily: SANS }}>Sent · {fmtDate(detail.received_at)}</span>
              <div className="flex-1" />
              <button
                onClick={() => handleCompose({
                  to_email: Array.isArray(detail.to) ? detail.to[0] : (detail.to || ''),
                  subject: detail.subject && !detail.subject.startsWith('Re:') ? 'Re: ' + detail.subject : (detail.subject || ''),
                  from_alias: detail.from_email || TARGET_MAILBOX,
                })}
                data-testid="sent-followup-compose"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-all"
                style={{ background: 'rgba(201,147,22,0.1)', border: '1px solid rgba(201,147,22,0.25)', color: GOLDB, fontFamily: SANS }}
              >
                <PenSquare size={12} /> Follow-up
              </button>
            </div>
          )}
        </div>

        {/* ── SUBSCRIBERS pane (slide-in right column) ── */}
        {showSubscribers && (
          <SubscribersPanel
            token={token}
            onComposeTo={data => { handleCompose(data); setShowSubscribers(false); }}
            onClose={() => setShowSubscribers(false)}
          />
        )}
      </div>

      {/* Subscriber profile modal */}
      {subscriberEmail && (
        <SubscriberProfileModal
          email={subscriberEmail}
          token={token}
          onComposeTo={data => { handleCompose(data); setSubscriberEmail(null); }}
          onOpenEmail={selectEmail}
          onClose={() => setSubscriberEmail(null)}
        />
      )}

      {/* Compose modal */}
      {composeOpen && (
        <AdminCompose
          isModal
          initialData={composeData}
          onClose={() => { setComposeOpen(false); setComposeData(null); }}
        />
      )}
    </div>
  );
}
